<template>
  <div class="ai-chat-view">
    <div class="chat-container">
      <!-- 채팅 헤더 -->
      <div class="chat-header">
        <div class="header-info">
          <div class="ai-avatar">
            <span class="avatar-text">분양<br>잇다</span>
          </div>
          <div class="header-text">
            <h1>분양잇다 AI</h1>
            <span class="status">온라인</span>
          </div>
        </div>
        <button v-if="step > 0" @click="resetChat" class="reset-btn">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          처음으로
        </button>
      </div>

      <!-- 채팅 메시지 영역 -->
      <div class="chat-messages" ref="messagesContainer">
        <!-- 메시지들 -->
        <div 
          v-for="(message, index) in messages" 
          :key="index" 
          :class="['message', message.type]"
        >
          <!-- AI 메시지 -->
          <template v-if="message.type === 'ai'">
            <div class="ai-avatar small">
              <span class="avatar-text">분양<br>잇다</span>
            </div>
            <div class="message-content">
              <p v-html="message.text"></p>
              
              <!-- 선택 버튼들 -->
              <div v-if="message.options && message.isActive" class="options-grid" :class="message.optionType">
                <button
                  v-for="option in message.options"
                  :key="option.value"
                  @click="selectOption(option)"
                  class="option-btn"
                >
                  {{ option.label }}
                </button>
              </div>

              <!-- 결과 카드들 -->
              <div v-if="message.jobs" class="jobs-grid">
                <div 
                  v-for="job in message.jobs" 
                  :key="job.id" 
                  class="job-card"
                  @click="showJobDetail(job)"
                >
                  <div class="job-thumbnail">
                    <img :src="job.thumbnail || 'https://picsum.photos/400/200?random=' + job.id" :alt="job.title" />
                    <div class="job-badges">
                      <span v-if="job.isPremium" class="badge premium">프리미엄</span>
                      <span v-if="job.isUrgent" class="badge urgent">급구</span>
                      <span v-if="job.isPick" class="badge pick">PICK</span>
                    </div>
                  </div>
                  <div class="job-info">
                    <h3>{{ job.title }}</h3>
                    <p class="job-meta">
                      <span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                        {{ job.region }}
                      </span>
                      <span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <rect x="3" y="3" width="18" height="18" rx="2"/>
                          <path d="M3 9h18"/>
                          <path d="M9 21V9"/>
                        </svg>
                        {{ job.industry }}
                      </span>
                    </p>
                    <p class="job-salary">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 6v12M9 9h6"/>
                      </svg>
                      {{ job.salaryType }} {{ job.salary }}
                    </p>
                    <p class="job-date">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <path d="M16 2v4M8 2v4M3 10h18"/>
                      </svg>
                      {{ formatDate(job.startDate) }} ~ {{ formatDate(job.deadline) }}
                    </p>
                  </div>
                  <div class="job-action">
                    <span>상세보기</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </div>
                </div>
              </div>

              <!-- 상세 정보 -->
              <div v-if="message.jobDetail" class="job-detail-card">
                <div class="detail-header">
                  <img :src="message.jobDetail.thumbnail || 'https://picsum.photos/600/300'" :alt="message.jobDetail.title" />
                  <div class="detail-badges">
                    <span v-if="message.jobDetail.isPremium" class="badge premium">프리미엄</span>
                    <span v-if="message.jobDetail.isUrgent" class="badge urgent">급구</span>
                  </div>
                </div>
                <div class="detail-body">
                  <h3>{{ message.jobDetail.title }}</h3>
                  <div class="detail-grid">
                    <div class="detail-item">
                      <span class="label">현장명</span>
                      <span class="value">{{ message.jobDetail.siteName }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="label">지역</span>
                      <span class="value">{{ message.jobDetail.region }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="label">업종</span>
                      <span class="value">{{ message.jobDetail.industry }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="label">직종</span>
                      <span class="value">{{ message.jobDetail.jobType }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="label">급여</span>
                      <span class="value highlight">{{ message.jobDetail.salaryType }} {{ message.jobDetail.salary }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="label">모집인원</span>
                      <span class="value">{{ message.jobDetail.headcount }}</span>
                    </div>
                    <div class="detail-item full">
                      <span class="label">주소</span>
                      <span class="value">{{ message.jobDetail.address }}</span>
                    </div>
                    <div class="detail-item full">
                      <span class="label">투입일</span>
                      <span class="value">{{ formatDate(message.jobDetail.startDate) }} ~ {{ formatDate(message.jobDetail.deadline) }}</span>
                    </div>
                    <div class="detail-item full" v-if="message.jobDetail.description">
                      <span class="label">상세내용</span>
                      <span class="value">{{ message.jobDetail.description }}</span>
                    </div>
                  </div>
                  <div class="detail-contact">
                    <p><strong>담당자:</strong> {{ message.jobDetail.contactName }}</p>
                    <p><strong>연락처:</strong> {{ message.jobDetail.contactPhone }}</p>
                  </div>
                  <div class="detail-actions">
                    <a :href="'tel:' + message.jobDetail.contactPhone" class="action-btn call">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                      </svg>
                      전화하기
                    </a>
                    <router-link :to="'/jobs/' + message.jobDetail.id" class="action-btn detail">
                      전체 상세보기
                    </router-link>
                  </div>
                </div>
              </div>

              <!-- 네비게이션 버튼 -->
              <div v-if="message.navigation && message.isActive" class="nav-buttons">
                <button 
                  v-if="message.navigation.includes('back')" 
                  @click="goBack" 
                  class="nav-btn back"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                  이전으로
                </button>
                <button 
                  v-if="message.navigation.includes('reset')" 
                  @click="resetChat" 
                  class="nav-btn reset"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  </svg>
                  처음으로
                </button>
                <button 
                  v-if="message.navigation.includes('list')" 
                  @click="backToList" 
                  class="nav-btn list"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
                  </svg>
                  목록으로
                </button>
              </div>
            </div>
          </template>

          <!-- 사용자 메시지 -->
          <template v-else>
            <div class="message-content user">
              <p>{{ message.text }}</p>
            </div>
          </template>
        </div>

        <!-- 로딩 표시 -->
        <div v-if="isTyping" class="message ai typing">
          <div class="ai-avatar small">
            <span class="avatar-text">분양<br>잇다</span>
          </div>
          <div class="message-content">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/services/firebase'

const messagesContainer = ref(null)
const messages = ref([])
const isTyping = ref(false)
const step = ref(0)

// 선택된 필터값들
const selectedFilters = ref({
  region: null,
  industry: null,
  jobType: null,
  experience: null
})

// 전체 공고 데이터
const allJobs = ref([])

// 히스토리 (뒤로가기용)
const history = ref([])

// 전체 공고 가져오기
const fetchAllJobs = async () => {
  try {
    const q = query(
      collection(db, 'jobs'),
      where('status', '==', 'active')
    )
    const snapshot = await getDocs(q)
    allJobs.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
  } catch (e) {
    console.error('공고 로딩 실패:', e)
  }
}

// 필터링된 공고 가져오기
const getFilteredJobs = (filters) => {
  let filtered = [...allJobs.value]
  
  if (filters.region) {
    filtered = filtered.filter(job => job.region === filters.region)
  }
  if (filters.industry) {
    filtered = filtered.filter(job => job.industry === filters.industry)
  }
  if (filters.jobType) {
    filtered = filtered.filter(job => job.jobType === filters.jobType)
  }
  if (filters.experience) {
    filtered = filtered.filter(job => job.experience === filters.experience)
  }
  
  return filtered
}

// 다음 단계에서 사용 가능한 옵션 가져오기
const getAvailableOptions = (currentFilters, nextField) => {
  const filtered = getFilteredJobs(currentFilters)
  const options = new Set()
  
  filtered.forEach(job => {
    if (job[nextField]) {
      options.add(job[nextField])
    }
  })
  
  return Array.from(options)
}

// 메시지 추가 (타이핑 효과)
const addMessage = async (message, delay = 500) => {
  isTyping.value = true
  await scrollToBottom()
  
  await new Promise(resolve => setTimeout(resolve, delay))
  
  isTyping.value = false
  messages.value.push(message)
  await scrollToBottom()
}

// 스크롤 아래로
const scrollToBottom = async () => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

// 이전 메시지 비활성화
const deactivatePreviousOptions = () => {
  messages.value.forEach(msg => {
    if (msg.type === 'ai') {
      msg.isActive = false
    }
  })
}

// 초기 메시지
const startChat = async () => {
  await addMessage({
    type: 'ai',
    text: '안녕하세요!<br><strong>분양잇다 AI</strong>입니다.<br><br>원하시는 조건의 분양 공고를 찾아드릴게요',
    isActive: true
  }, 300)

  // 사용 가능한 지역 가져오기
  const regions = getAvailableOptions({}, 'region')

  await addMessage({
    type: 'ai',
    text: '어떤 <strong>지역</strong>의 공고를 찾으시나요?',
    options: regions.map(r => ({ label: r, value: r })),
    optionType: 'region',
    isActive: true
  }, 600)
  
  step.value = 1
}

// 옵션 선택
const selectOption = async (option) => {
  // 히스토리 저장
  history.value.push({
    step: step.value,
    filters: { ...selectedFilters.value },
    messagesLength: messages.value.length
  })

  // 이전 옵션 비활성화
  deactivatePreviousOptions()

  // 사용자 선택 메시지 추가
  messages.value.push({
    type: 'user',
    text: option.label
  })
  
  await scrollToBottom()

  // 단계별 처리
  if (step.value === 1) {
    // 지역 선택 완료
    selectedFilters.value.region = option.value
    await showIndustryOptions()
  } else if (step.value === 2) {
    // 업종 선택 완료
    selectedFilters.value.industry = option.value === '전체' ? null : option.value
    await showJobTypeOptions()
  } else if (step.value === 3) {
    // 직종 선택 완료
    selectedFilters.value.jobType = option.value === '전체' ? null : option.value
    await showExperienceOptions()
  } else if (step.value === 4) {
    // 경력 선택 완료
    selectedFilters.value.experience = option.value === '전체' ? null : option.value
    await searchJobs()
  }
}

// 업종 선택
const showIndustryOptions = async () => {
  // 현재 지역에서 사용 가능한 업종만 가져오기
  const industries = getAvailableOptions(
    { region: selectedFilters.value.region }, 
    'industry'
  )
  
  const options = ['전체', ...industries]

  await addMessage({
    type: 'ai',
    text: `<strong>${selectedFilters.value.region}</strong> 지역을 선택하셨네요<br><br>어떤 <strong>분양 유형</strong>을 찾으시나요?`,
    options: options.map(i => ({ label: i, value: i })),
    optionType: 'industry',
    isActive: true
  })
  
  step.value = 2
}

// 직종 선택
const showJobTypeOptions = async () => {
  // 현재 지역+업종에서 사용 가능한 직종만 가져오기
  const jobTypes = getAvailableOptions(
    { 
      region: selectedFilters.value.region,
      industry: selectedFilters.value.industry
    }, 
    'jobType'
  )
  
  const options = ['전체', ...jobTypes]
  const industryText = selectedFilters.value.industry || '전체 업종'
  
  await addMessage({
    type: 'ai',
    text: `<strong>${industryText}</strong>을 선택하셨네요<br><br>어떤 <strong>포지션</strong>을 원하시나요?`,
    options: options.map(j => ({ label: j, value: j })),
    optionType: 'jobType',
    isActive: true
  })
  
  step.value = 3
}

// 경력 선택
const showExperienceOptions = async () => {
  // 현재 지역+업종+직종에서 사용 가능한 경력만 가져오기
  const experiences = getAvailableOptions(
    { 
      region: selectedFilters.value.region,
      industry: selectedFilters.value.industry,
      jobType: selectedFilters.value.jobType
    }, 
    'experience'
  )
  
  const options = ['전체', ...experiences]
  const jobTypeText = selectedFilters.value.jobType || '전체 직종'
  
  await addMessage({
    type: 'ai',
    text: `<strong>${jobTypeText}</strong> 포지션을 선택하셨네요<br><br>마지막으로 <strong>경력 조건</strong>을 선택해주세요`,
    options: options.map(e => ({ label: e, value: e })),
    optionType: 'experience',
    isActive: true
  })
  
  step.value = 4
}

// 공고 검색
const searchJobs = async () => {
  await addMessage({
    type: 'ai',
    text: '조건에 맞는 공고를 찾고 있어요...',
    isActive: false
  }, 300)

  try {
    // 필터링된 공고 가져오기
    let jobs = getFilteredJobs(selectedFilters.value)
    
    // 정렬 (프리미엄 > 픽 > 급구 > 일반)
    jobs.sort((a, b) => {
      if (a.isPremium !== b.isPremium) return b.isPremium ? 1 : -1
      if (a.isPick !== b.isPick) return b.isPick ? 1 : -1
      if (a.isUrgent !== b.isUrgent) return b.isUrgent ? 1 : -1
      return 0
    })
    
    // 마지막 메시지 제거 (로딩 메시지)
    messages.value.pop()
    
    // 결과 표시
    const filterSummary = [
      selectedFilters.value.region,
      selectedFilters.value.industry || '전체 업종',
      selectedFilters.value.jobType || '전체 직종',
      selectedFilters.value.experience || '전체 경력'
    ].join(' / ')

    await addMessage({
      type: 'ai',
      text: `<strong>${filterSummary}</strong><br>조건에 맞는 공고 <strong>${jobs.length}건</strong>을 찾았어요<br><br>관심있는 공고를 선택해보세요`,
      jobs: jobs.slice(0, 10), // 최대 10개
      navigation: ['back', 'reset'],
      isActive: true
    }, 400)
    
    step.value = 5
  } catch (e) {
    console.error('검색 실패:', e)
    messages.value.pop()
    await addMessage({
      type: 'ai',
      text: '검색 중 오류가 발생했어요<br>다시 시도해주세요',
      navigation: ['reset'],
      isActive: true
    })
  }
}

// 공고 상세보기
const showJobDetail = async (job) => {
  // 히스토리 저장
  history.value.push({
    step: step.value,
    filters: { ...selectedFilters.value },
    messagesLength: messages.value.length
  })

  deactivatePreviousOptions()

  messages.value.push({
    type: 'user',
    text: `${job.title} 상세보기`
  })

  await addMessage({
    type: 'ai',
    text: `<strong>${job.title}</strong> 상세정보예요`,
    jobDetail: job,
    navigation: ['list', 'reset'],
    isActive: true
  })
  
  step.value = 6
}

// 뒤로가기
const goBack = async () => {
  if (history.value.length === 0) return
  
  const prev = history.value.pop()
  
  // 메시지 복원
  messages.value = messages.value.slice(0, prev.messagesLength)
  selectedFilters.value = prev.filters
  step.value = prev.step
  
  // 마지막 메시지 활성화
  const lastAiMessage = [...messages.value].reverse().find(m => m.type === 'ai')
  if (lastAiMessage) {
    lastAiMessage.isActive = true
  }
  
  await scrollToBottom()
}

// 목록으로 돌아가기
const backToList = async () => {
  goBack()
}

// 처음으로
const resetChat = async () => {
  messages.value = []
  selectedFilters.value = {
    region: null,
    industry: null,
    jobType: null,
    experience: null
  }
  history.value = []
  step.value = 0
  
  await startChat()
}

// 날짜 포맷
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return `${date.getMonth() + 1}/${date.getDate()}`
}

// 초기화
onMounted(async () => {
  await fetchAllJobs()
  await startChat()
})
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.ai-chat-view {
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;
}

.chat-container {
  max-width: 800px;
  margin: 0 auto;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 40px);
}

/* 헤더 */
.chat-header {
  background: white;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e8eb;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ai-avatar {
  width: 48px;
  height: 48px;
  background: #191f28;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-text {
  font-size: 11px;
  font-weight: 700;
  color: white;
  line-height: 1.2;
  text-align: center;
  letter-spacing: -0.3px;
}

.ai-avatar.small {
  width: 36px;
  height: 36px;
  border-radius: 10px;
}

.ai-avatar.small .avatar-text {
  font-size: 9px;
}

.header-text h1 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #191f28;
  margin: 0;
}

.status {
  font-size: 0.875rem;
  color: #10b981;
  display: flex;
  align-items: center;
  gap: 6px;
}

.status::before {
  content: '';
  width: 6px;
  height: 6px;
  background: #10b981;
  border-radius: 50%;
}

.reset-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f8f9fa;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #4e5968;
  cursor: pointer;
  transition: all 0.2s;
}

.reset-btn:hover {
  background: #191f28;
  border-color: #191f28;
  color: #fff;
}

.reset-btn svg {
  width: 16px;
  height: 16px;
}

/* 메시지 영역 */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: #fafbfc;
}

/* 메시지 */
.message {
  display: flex;
  gap: 12px;
  animation: messageIn 0.3s ease-out;
}

@keyframes messageIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message.user {
  justify-content: flex-end;
}

.message-content {
  max-width: 85%;
}

.message-content p {
  background: white;
  padding: 16px 20px;
  border-radius: 16px;
  border-top-left-radius: 4px;
  font-size: 1rem;
  line-height: 1.6;
  color: #191f28;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin: 0;
  border: 1px solid #f1f3f5;
}

.message-content.user p {
  background: #191f28;
  color: white;
  border-radius: 16px;
  border-top-right-radius: 4px;
  border-top-left-radius: 16px;
  border: none;
}

/* 옵션 버튼 그리드 */
.options-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.options-grid.region {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
}

.option-btn {
  padding: 12px 20px;
  background: white;
  border: 2px solid #e5e8eb;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #4e5968;
  cursor: pointer;
  transition: all 0.2s;
}

.option-btn:hover {
  border-color: #191f28;
  color: #191f28;
  background: #f8f9fa;
  transform: translateY(-2px);
}

/* 공고 카드 그리드 */
.jobs-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
}

.job-card {
  background: white;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid #f1f3f5;
}

.job-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  border-color: #e5e8eb;
}

.job-card .job-thumbnail {
  position: relative;
  height: 140px;
  overflow: hidden;
}

.job-card .job-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.job-badges {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  gap: 6px;
}

.badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
}

.badge.premium {
  background: #ff9500;
  color: white;
}

.badge.urgent {
  background: #dc2626;
  color: white;
}

.badge.pick {
  background: #7c3aed;
  color: white;
}

.job-info {
  padding: 16px;
}

.job-info h3 {
  font-size: 1.05rem;
  font-weight: 700;
  color: #191f28;
  margin: 0 0 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.job-meta {
  display: flex;
  gap: 12px;
  font-size: 0.875rem;
  color: #6b7684;
  margin: 0 0 8px 0;
}

.job-meta span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.job-meta svg {
  flex-shrink: 0;
}

.job-salary {
  font-size: 0.95rem;
  font-weight: 700;
  color: #191f28;
  margin: 0 0 6px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.job-salary svg {
  flex-shrink: 0;
}

.job-date {
  font-size: 0.85rem;
  color: #94a3b8;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.job-date svg {
  flex-shrink: 0;
}

.job-action {
  padding: 12px 16px;
  background: #fafbfc;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #191f28;
}

.job-action svg {
  flex-shrink: 0;
}

/* 상세 카드 */
.job-detail-card {
  background: white;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-top: 16px;
  border: 1px solid #f1f3f5;
}

.detail-header {
  position: relative;
  height: 200px;
}

.detail-header img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-badges {
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  gap: 8px;
}

.detail-body {
  padding: 24px;
}

.detail-body h3 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #191f28;
  margin: 0 0 20px 0;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-item.full {
  grid-column: 1 / -1;
}

.detail-item .label {
  font-size: 0.85rem;
  color: #94a3b8;
  font-weight: 500;
}

.detail-item .value {
  font-size: 1rem;
  color: #191f28;
  font-weight: 600;
}

.detail-item .value.highlight {
  color: #191f28;
  font-weight: 700;
}

/* 상세내용은 줄바꿈과 띄어쓰기 유지 */
.detail-item.full .value {
  white-space: pre-wrap;
  line-height: 1.6;
  font-weight: 400;
}

.detail-contact {
  background: #fafbfc;
  padding: 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  border: 1px solid #f1f3f5;
}

.detail-contact p {
  margin: 0;
  padding: 6px 0;
  font-size: 0.95rem;
  color: #4e5968;
}

.detail-actions {
  display: flex;
  gap: 12px;
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
}

.action-btn.call {
  background: #10b981;
  color: white;
}

.action-btn.call:hover {
  background: #059669;
}

.action-btn.call svg {
  width: 18px;
  height: 18px;
}

.action-btn.detail {
  background: #191f28;
  color: white;
}

.action-btn.detail:hover {
  background: #333d4b;
}

/* 네비게이션 버튼 */
.nav-buttons {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.nav-btn svg {
  width: 16px;
  height: 16px;
}

.nav-btn.back {
  background: #f8f9fa;
  border: 1px solid #e5e8eb;
  color: #4e5968;
}

.nav-btn.back:hover {
  background: #191f28;
  border-color: #191f28;
  color: #fff;
}

.nav-btn.reset {
  background: #fff1f0;
  border: 1px solid #ffccc7;
  color: #dc2626;
}

.nav-btn.reset:hover {
  background: #dc2626;
  border-color: #dc2626;
  color: #fff;
}

.nav-btn.list {
  background: #f0f4ff;
  border: 1px solid #d6e4ff;
  color: #4f46e5;
}

.nav-btn.list:hover {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #fff;
}

/* 타이핑 인디케이터 */
.typing-indicator {
  display: flex;
  gap: 6px;
  padding: 16px 20px;
  background: white;
  border-radius: 16px;
  border-top-left-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #f1f3f5;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #cbd5e1;
  border-radius: 50%;
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-8px);
    opacity: 1;
  }
}

/* 반응형 */
@media (max-width: 768px) {
  .ai-chat-view {
    padding: 0;
  }

  .chat-container {
    border-radius: 0;
    height: 100vh;
  }

  .chat-header {
    padding: 16px;
  }

  .header-text h1 {
    font-size: 1.1rem;
  }

  .reset-btn span {
    display: none;
  }

  .chat-messages {
    padding: 16px;
  }

  .message-content {
    max-width: 90%;
  }

  .options-grid.region {
    grid-template-columns: repeat(3, 1fr);
  }

  .option-btn {
    padding: 10px 14px;
    font-size: 0.9rem;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .detail-actions {
    flex-direction: column;
  }

  .nav-buttons {
    flex-wrap: wrap;
  }
}
</style>